import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import {
  ISupportCateogryForm,
} from "interfaces";
import { HttpError, useCreate } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";


const CreateSupportCategorySchema = z.object({
  is_active: z.boolean(),
  name_en: z.string().min(1, "This field is required"),
  name_vi: z.string().min(1, "This field is required"),
  order_number: z.number({
    invalid_type_error: "Position must be a number",
    required_error: "Position is required",
  })
  .min(1, {message:"Position must be equal or greater than 1"})
  .nonnegative({ message: "Position must be a positive number" }),
});

export const CreateSupportCategoryForm = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ISupportCateogryForm, HttpError, ISupportCateogryForm>({
    defaultValues: {
      name_en: "",
      name_vi: "",
      is_active: true,
      order_number: 1
    },
    resolver: zodResolver(CreateSupportCategorySchema),
  });

  const { mutate, isLoading: isLoading } = useCreate();
  const navigate = useNavigate();

  const onSubmit = (values: ISupportCateogryForm) => {
    const submitValues = { ...values };

    mutate(
      {
        resource: "cs/support-center/categories",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Create successfully");
          navigate(`/cs/support-category`);
        },
      }
    );
  };

  return (
    <div className="w-full ">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
              <div className="sm:col-span-3">
                <Label required id="is_active" name="Status" />
                <Controller
                  control={control}
                  name="is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <SwitchButton checked={value} onChange={onChange} />
                  )}
                />
              </div>

              <div className="sm:col-span-3">
                <Label required id="order_number" name="Position" />
                <Input
                  id="order_number"
                  type="number"
                  min={1}
                  placeholder="Position"
                  error={errors["order_number"]}
                  {...register("order_number", {
                    valueAsNumber: true
                  })}
                />
                {errors["order_number"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.order_number.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="name_vi" name="Categories name vie" />
                <Input
                  id="name_vi"
                  type="text"
                  placeholder="Categories name vie"
                  error={errors["name_vi"]}
                  {...register("name_vi")}
                />
                {errors["name_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.name_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="name_en" name="Categories name eng" />
                <Input
                  id="name_en"
                  type="text"
                  placeholder="Categories name eng"
                  error={errors["name_en"]}
                  {...register("name_en")}
                />
                {errors["name_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.name_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/cs/support-category`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
        </div>
        {isLoading ? <FullScreenLoading /> : <></>}
      </form>
    </div>
  );
};